import { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Reel Purpose picker — one-click templates that bundle badges, music
 * mood, transitions, captions and 3D-text flourishes for the 5 most
 * common real-estate social-media scenarios:
 *
 *   Teaser · Just Listed · Open House · Price Drop · Just Sold
 *
 * Auto-picks the top photos via Smart Reel by default; lets the
 * realtor switch to manual photo selection if they want control.
 *
 * Inspired by VideoTour.AI's "AI Social Media Reels" picker shown in
 * https://youtu.be/gbf8XQFToF4 .
 */
export default function ReelPurposePicker({ listingId, listingImages = [], onClose, onCreated }) {
  const [purposes, setPurposes] = useState([]);
  const [picked, setPicked] = useState(null);            // purpose object
  const [extraInstructions, setExtraInstructions] = useState("");
  const [selectMode, setSelectMode] = useState("auto");  // "auto" | "manual"
  const [manualIds, setManualIds] = useState(() => new Set());
  const [extraAspects, setExtraAspects] = useState(() => new Set()); // "9_16" | "1_1"
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    api.get("/reel-purposes")
      .then(({ data }) => setPurposes(data?.purposes || []))
      .catch((e) => setLoadError(e?.response?.data?.detail || "Failed to load purposes"));
  }, []);

  const canSubmit = useMemo(() => {
    if (!picked || busy) return false;
    if (selectMode === "manual" && manualIds.size < 3) return false;
    return true;
  }, [picked, busy, selectMode, manualIds]);

  const toggleManual = (id) => {
    setManualIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < 8) next.add(id);
      return next;
    });
  };

  const toggleAspect = (a) => {
    setExtraAspects((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  };

  const submit = async () => {
    if (!picked) return;
    setBusy(true);
    const tId = toast.loading(`Queuing ${picked.label} reel…`, {
      description:
        selectMode === "auto"
          ? "GPT picks the best photos · writes a tailored VO · applies the badge & music mood. ~60-90s of Magnific."
          : "Building reel with your manually-picked photos and the purpose's defaults. ~60-90s of Magnific.",
      duration: Infinity,
    });
    try {
      const body = {
        listing_id: listingId,
        extra_instructions: extraInstructions.trim() || null,
        target_shots: 6,
        include_voiceover: true,
        model: "seedance-lite-720p",
        aspect_ratio: "widescreen_16_9",
        extra_aspects: extraAspects.size ? Array.from(extraAspects) : null,
      };
      if (selectMode === "manual") body.photo_ids = Array.from(manualIds);
      const { data } = await api.post(`/reel-purposes/${picked.id}/build`, body);
      toast.success(`${picked.label} reel queued.`, {
        id: tId,
        description: `Check the Library tab. Build version: ${data?.id?.slice(0, 8) || ""}`,
        duration: 7000,
      });
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build reel", {
        id: tId,
        duration: 8000,
      });
    } finally {
      setBusy(false);
    }
  };

  const iconFor = (name) => {
    const I = Icons[name];
    return I || Icons.Sparkles;
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reel-purpose-overlay"
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="reel-purpose-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <Icons.Wand2 size={15} className="text-amber-400" />
            <div>
              <div className="text-[13px] text-white font-medium">Build a reel · pick a purpose</div>
              <div className="text-[10px] text-zinc-500">
                One click. Badges, music, transitions, captions — auto-tuned for the scenario.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="reel-purpose-close"
          >
            <Icons.X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {loadError && (
            <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-md p-3">
              {loadError}
            </div>
          )}

          {/* Purpose cards */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Reel purpose</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {purposes.map((p) => {
                const IconCmp = iconFor(p.icon);
                const isPicked = picked?.id === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPicked(p)}
                    className={`text-left rounded-lg border p-3 transition-all ${
                      isPicked
                        ? "border-amber-500/70 bg-amber-500/10 ring-2 ring-amber-500/30"
                        : "border-white/10 bg-black/40 hover:border-white/30"
                    }`}
                    data-testid={`reel-purpose-card-${p.id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <IconCmp size={13} className={isPicked ? "text-amber-300" : "text-zinc-400"} />
                      <div className="text-[12px] text-white font-medium">{p.label}</div>
                    </div>
                    <div className="text-[10px] text-zinc-500 leading-snug min-h-[28px]">
                      {p.blurb}
                    </div>
                    <div className="mt-2 flex items-center gap-1 flex-wrap">
                      {p.badge_text && (
                        <span className="text-[8px] tracking-[0.18em] uppercase text-amber-200 px-1 py-0.5 rounded border border-amber-400/30 bg-amber-500/10">
                          {p.badge_text}
                        </span>
                      )}
                      {p.badge_style === "animated_3d" && (
                        <span className="text-[8px] tracking-[0.18em] uppercase text-fuchsia-200 px-1 py-0.5 rounded border border-fuchsia-400/30 bg-fuchsia-500/10">
                          3D intro
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Photo selection mode */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Photos</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setSelectMode("auto")}
                className={`text-left rounded-lg border p-3 transition-all ${
                  selectMode === "auto"
                    ? "border-white bg-white text-black"
                    : "border-white/10 bg-black/40 text-white hover:border-white/30"
                }`}
                data-testid="reel-purpose-photos-auto"
              >
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider">
                  <Icons.Sparkles size={11} /> Auto-pick (GPT)
                </div>
                <div className={`text-[10px] mt-1 leading-tight ${selectMode === "auto" ? "text-black/60" : "text-zinc-500"}`}>
                  GPT scans photos, picks the top 6 with scene diversity.
                </div>
              </button>
              <button
                onClick={() => setSelectMode("manual")}
                className={`text-left rounded-lg border p-3 transition-all ${
                  selectMode === "manual"
                    ? "border-white bg-white text-black"
                    : "border-white/10 bg-black/40 text-white hover:border-white/30"
                }`}
                data-testid="reel-purpose-photos-manual"
              >
                <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider">
                  <Icons.MousePointerClick size={11} /> Manual pick
                </div>
                <div className={`text-[10px] mt-1 leading-tight ${selectMode === "manual" ? "text-black/60" : "text-zinc-500"}`}>
                  Pick 3–8 photos yourself. Used in the order you click them.
                </div>
              </button>
            </div>

            {selectMode === "manual" && (
              <div>
                <div className="text-[10px] text-zinc-500 mb-2">
                  {manualIds.size} selected · click photos in the order you want them to appear
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-56 overflow-y-auto p-1 bg-black/40 rounded-md">
                  {listingImages.length === 0 ? (
                    <div className="col-span-full text-[11px] text-zinc-500 text-center py-6">
                      No photos on this listing yet.
                    </div>
                  ) : (
                    listingImages.map((img) => {
                      const isPicked = manualIds.has(img.id);
                      const order = isPicked ? Array.from(manualIds).indexOf(img.id) + 1 : 0;
                      return (
                        <button
                          key={img.id}
                          onClick={() => toggleManual(img.id)}
                          className={`relative aspect-square rounded-md overflow-hidden border-2 ${
                            isPicked ? "border-amber-400" : "border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`reel-purpose-photo-${img.id}`}
                        >
                          {img.url && (
                            <img src={img.url} alt="" className="w-full h-full object-cover" />
                          )}
                          {isPicked && (
                            <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-amber-400 text-black text-[10px] font-bold flex items-center justify-center">
                              {order}
                            </div>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Extra instructions */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2 flex items-center gap-1.5">
              <Icons.MessageSquare size={11} className="text-amber-400" />
              Extra instructions <span className="text-[9px] text-zinc-600">(optional)</span>
            </div>
            <textarea
              value={extraInstructions}
              onChange={(e) => setExtraInstructions(e.target.value.slice(0, 300))}
              rows={2}
              placeholder={`e.g. "Emphasise the luxury kitchen and the backyard pool. Mention the chef's gas range."`}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-[12px] text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/40 resize-none"
              data-testid="reel-purpose-extra-instructions"
            />
            <div className="text-[9px] text-zinc-600 text-right">{extraInstructions.length}/300</div>
          </div>

          {/* Extra aspect ratios */}
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2 flex items-center gap-1.5">
              <Icons.Frame size={11} className="text-amber-400" />
              Auto-export social formats <span className="text-[9px] text-zinc-600">(optional)</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { id: "9_16", label: "9:16 · Reels / TikTok / Shorts" },
                { id: "1_1", label: "1:1 · Square feed" },
              ].map((a) => (
                <button
                  key={a.id}
                  onClick={() => toggleAspect(a.id)}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition-all ${
                    extraAspects.has(a.id)
                      ? "bg-amber-500/20 border-amber-400/50 text-amber-200"
                      : "bg-black/40 border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                  data-testid={`reel-purpose-aspect-${a.id}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between gap-3 bg-black/30">
          <div className="text-[10px] text-zinc-500 leading-snug">
            {picked
              ? `Will build a ${picked.label} reel with ${selectMode === "auto" ? "GPT-picked photos" : `${manualIds.size} manually-picked photos`}.`
              : "Pick a purpose to continue."}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-[11px] text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={!canSubmit}
              className="px-4 py-1.5 rounded-md text-[11px] bg-amber-500 text-black font-medium hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="reel-purpose-go"
            >
              {busy ? (
                <>
                  <Icons.Loader2 size={11} className="animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Icons.Wand2 size={11} />
                  Build {picked?.label || "reel"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
